<template>
  <div
    class="custody-panel"
    :class="{ 'dashboard-node--highlight': highlighted }"
  >
    <div
      v-if="variant === 'card'"
      class="custody-panel__card"
    >
      <div class="custody-panel__card-head mb-4">
        <div class="custody-panel__doc">
          <v-icon class="custody-panel__doc-icon">
            mdi-text-box-outline
          </v-icon>
          <b>{{ primaryRow.name }}</b>
        </div>
        <BaseStatusPill
          v-if="primaryRow.custodyStatus"
          :label="primaryRow.custodyStatus.label"
          :variant="primaryRow.custodyStatus.variant"
        />
      </div>
      <v-divider />
      <BaseFigures
        :items="figures"
        :instance="instance"
        class="custody-panel__card-figures"
      />
      <BaseButtonAction
        v-if="primaryRow.action"
        v-bind="primaryRow.action"
        @click.native="handleAction({ action: primaryRow.action, row: primaryRow })"
      />
    </div>

    <BaseDashboardTable
      v-else
      :columns="columns"
      :rows="rows"
      :item-key="itemKey"
      :hide-default-footer="false"
      :server-items-length="serverItemsLength"
      :options="options"
      @update:options="$emit('update:options', { instance, options: $event })"
    >
      <template v-slot:cell="{ column, row }">
        <BaseDashboardTableCell
          :column="column"
          :row="row"
          :data-field="cellDataField(column, row)"
          :data-row-key="rowKeyOf(row)"
          @detail="$emit('row-detail', $event)"
          @action="handleAction"
        >
          <div
            v-if="column.key === 'name' && row.ref"
            class="custody-panel__doc"
          >
            <v-icon class="custody-panel__doc-icon">
              mdi-text-box-outline
            </v-icon>
            <span>
              <b>{{ row.name }}</b>
              <span class="custody-panel__doc-ref">{{ row.ref }}</span>
            </span>
          </div>
          <span v-else>{{ row[column.key] }}</span>
        </BaseDashboardTableCell>
      </template>
    </BaseDashboardTable>
  </div>
</template>

<script>
import BaseDashboardTable from './BaseDashboardTable.vue'
import BaseDashboardTableCell from './BaseDashboardTableCell.vue'
import BaseFigures from './BaseFigures.vue'
import BaseStatusPill from './BaseStatusPill.vue'
import BaseButtonAction from './BaseButtonAction.vue'
import { sanitizeDataField } from './dataFieldUtils'
import DashboardHighlightOnRender from '../../mixins/DashboardHighlightOnRender'

export default {
  name: 'BaseCustodyPanel',
  mixins: [DashboardHighlightOnRender],
  components: { BaseDashboardTable, BaseDashboardTableCell, BaseFigures, BaseStatusPill, BaseButtonAction },
  props: {
    columns: {
      type: Array,
      required: false,
      default: () => []
    },
    rows: {
      type: Array,
      required: true
    },
    figures: {
      type: Array,
      required: false,
      default: () => []
    },
    variant: {
      type: String,
      required: false,
      default: 'list',
      validator: (value) => ['list', 'card'].includes(value)
    },
    itemKey: {
      type: String,
      required: false,
      default: undefined
    },
    instance: {
      type: String,
      required: false,
      default: ''
    },
    serverItemsLength: {
      type: Number,
      required: false,
      default: -1
    },
    options: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  computed: {
    primaryRow() {
      return this.rows[0] || {}
    }
  },
  methods: {
    handleAction(payload) {
      this.$emit('action', payload)
    },
    rowKeyOf(row) {
      if (!row) {
        return undefined
      }
      if (row._rowKey) {
        return sanitizeDataField(row._rowKey)
      }
      if (!this.itemKey) {
        return undefined
      }
      const value = this.itemKey.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row)
      return sanitizeDataField(value)
    },
    cellDataField(column, row) {
      const rowKey = this.rowKeyOf(row)
      return rowKey ? `${sanitizeDataField(column.key)}-${rowKey}` : sanitizeDataField(column.key)
    }
  }
}
</script>

<style lang="scss">
.custody-panel {
  &__doc {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__doc-icon {
    color: var(--ds-color-black-040);
    font-size: 1.1rem;
    flex: none;
  }

  &__doc-ref {
    display: block;
    font-size: 0.75rem;
    color: var(--ds-color-black-055);
  }

  &__card {
    border: 1px solid var(--ds-color-black-010);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
}
</style>
